/** Internal workflow fields that should not appear in user-facing audit history. */
const INTERNAL_ORDER_HISTORY_FIELDS = [
    'acceptedNewOrder',
    'orderReviewed',
    'orderReady',
    'reviewed',
];

const isBlankHistoryValue = (value) =>
    value === null || value === undefined || String(value).trim() === '';

const isBoolishHistoryValue = (value) => {
    const v = String(value ?? '').trim().toLowerCase();
    return v === 'true' || v === 'false' || v === '1' || v === '0';
};

const historyEntryTextHaystack = (entry) =>
    [
        entry?.actionDetail,
        entry?.actionType,
        entry?.fieldName,
        entry?.changedField,
        entry?.propertyName,
        entry?.previousValue,
        entry?.newValue,
    ]
        .map((value) => String(value ?? ''))
        .join(' ');

export const isInternalOrderHistoryEntry = (entry) => {
    const haystack = historyEntryTextHaystack(entry);
    if (!haystack.trim()) return false;

    return INTERNAL_ORDER_HISTORY_FIELDS.some((field) => {
        const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const quoted = new RegExp(`['"]${escaped}['"]`);
        const bare = new RegExp(`\\b${escaped}\\b`);
        return quoted.test(haystack) || bare.test(haystack);
    });
};

/**
 * Boolean true/false audit rows that were not upgraded by a real Order Access hint.
 * Confirm & Move (`acceptedNewOrder`), Review, and Ready all log this way and must
 * not appear as "Order Access link added".
 */
export const isUnmatchedBooleanWorkflowEntry = (entry) => {
    if (entry?._orderAccessHint || entry?._syntheticOrderAccessHint) return false;

    const type = String(entry?.actionType || '').trim().toUpperCase();
    if (
        type
        && type !== 'ORDER_STATUS'
        && type !== 'ORDER_ACCESS'
        && type !== 'ORDER_URL'
    ) {
        return false;
    }

    const previousBlankOrBool = isBlankHistoryValue(entry?.previousValue)
        || isBoolishHistoryValue(entry?.previousValue);
    const nextBlankOrBool = isBlankHistoryValue(entry?.newValue)
        || isBoolishHistoryValue(entry?.newValue);
    const hasBooleanPlaceholder = isBoolishHistoryValue(entry?.previousValue)
        || isBoolishHistoryValue(entry?.newValue);

    return hasBooleanPlaceholder && previousBlankOrBool && nextBlankOrBool;
};

/** History rows after 6:00 PM local time are hidden from the modal and unread badge. */
const HISTORY_CUTOFF_HOUR = 18;

export const isAfterSixPmHistoryEntry = (entry) => {
    const changedAt = entry?.changedAt;
    if (!changedAt) return false;
    const date = new Date(changedAt);
    if (Number.isNaN(date.getTime())) return false;
    return date.getHours() >= HISTORY_CUTOFF_HOUR;
};

export const filterOrderHistoryEntries = (entries) => {
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry) =>
        !isInternalOrderHistoryEntry(entry) && !isAfterSixPmHistoryEntry(entry)
    );
};

/** Call after Order Access hints are applied so real link add/update/remove rows are kept. */
export const dropUnmatchedBooleanWorkflowHistory = (entries) => {
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry) =>
        !isInternalOrderHistoryEntry(entry) && !isUnmatchedBooleanWorkflowEntry(entry)
    );
};

const isDeletedCommentEntry = (entry) =>
    entry?.actionType === 'ORDER_COMMENT' && entry?.actionDetail === 'Comment deleted';

const isCommentEntry = (entry) => entry?.actionType === 'ORDER_COMMENT';

const looksLikeOrderAccessDetail = (entry) => {
    const detail = String(entry?.actionDetail || '').toLowerCase();
    return /order\s*access|order\s*url|folder\s*url|file\s*url|url\s*name/.test(detail);
};

const isOrderAccessEntry = (entry) => {
    if (entry?._orderAccessHint || entry?._syntheticOrderAccessHint) return true;

    const type = String(entry?.actionType || '').trim().toUpperCase();
    if (type === 'ORDER_ACCESS' || type === 'ORDER_URL') {
        // Boolean-only ORDER_ACCESS rows are workflow flags mis-tagged by the backend.
        return !isUnmatchedBooleanWorkflowEntry(entry);
    }

    // Do not treat leftover ORDER_STATUS true/false rows as Order Access.
    // Those are created by Confirm & Move / Review / Ready, not by adding a link.
    if (looksLikeOrderAccessDetail(entry) && !isUnmatchedBooleanWorkflowEntry(entry)) {
        return true;
    }

    return false;
};

/** Status tag fields are stored as JSON arrays; an empty/absent value means no status set. */
const STATUS_ACTION_TYPES = ['ORDER_STATUS', 'LINE_ITEM_STATUS'];
const NO_STATUS_LABEL = 'No Status';
const NO_COMMENT_LABEL = 'No Comment';

/**
 * The backend records every per-line status save as LINE_ITEM_STATUS, even when the
 * saved tag actually belongs to the Order Status column. We recover the real column
 * from the tag labels so the history reflects where the tag was truly saved.
 * Keep these lists in sync with ORDER_STATUS_TAGS / LINE_STATUS_TAGS in the Orders views.
 */
const ORDER_STATUS_LABELS = new Set([
    'CC Hold', 'Wire Hold', 'Credit Hold', 'PPA', 'Paid', 'Partial', 'May Ship Early',
    'CoT Required', 'PMA', 'AS9102', 'Burn Test', 'No Overage', 'Specific Lengths Required',
    'Do Not Insure', 'Shipping Date Required', 'No Charge', 'AOG', 'Hot Order', 'PO Review',
]);

const LINE_STATUS_LABELS = new Set([
    'Released', 'Shipped', 'In Kitting', 'In Process', 'In Testing', 'In Outgoing Inspection',
    'Not Released', 'On Hold Cart', 'Staging Cart', 'Waiting On Components', 'PMA', 'RMA',
    'ENG Review', 'Charge',
]);

/** Parse a stored status value (JSON array of tag objects/strings) into a list of labels. */
const parseStatusLabels = (value) => {
    if (value === null || value === undefined) return [];
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[]') return [];
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed
                .map((tag) => (tag && typeof tag === 'object' ? String(tag.label ?? '').trim() : String(tag ?? '').trim()))
                .filter((label) => label !== '');
        }
    } catch (err) {
        // Not JSON — treat the raw string as a single label.
        return [trimmed];
    }
    return [];
};

/**
 * Parse a stored status value into a list of tag objects, preserving color/icon so the
 * history cell can still render colored badges after we slice out the changed tags.
 */
const parseStatusTags = (value) => {
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[]') return [];
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed
                .map((tag) => (tag && typeof tag === 'object'
                    ? { ...tag, label: String(tag.label ?? '').trim() }
                    : { label: String(tag ?? '').trim() }))
                .filter((tag) => tag.label !== '');
        }
    } catch (err) {
        return [{ label: trimmed }];
    }
    return [];
};

const serializeStatusTags = (tags) => JSON.stringify(tags);

const isStatusEntry = (entry) => STATUS_ACTION_TYPES.includes(entry?.actionType);

/**
 * Resolve the real status column for a history entry by inspecting the saved tag labels.
 * Falls back to the backend-provided actionType when a tag is ambiguous (belongs to both
 * lists, e.g. "PMA") or unrecognized.
 */
export const resolveStatusActionType = (entry) => {
    // Confirmed Order Access (hint, ORDER_ACCESS type, or access/url detail) wins over ORDER_STATUS.
    if (isOrderAccessEntry(entry)) return 'ORDER_ACCESS';
    if (!isStatusEntry(entry)) return entry?.actionType;

    const labels = [...parseStatusLabels(entry?.previousValue), ...parseStatusLabels(entry?.newValue)];
    if (labels.length === 0) return entry?.actionType;

    const isOrderStatus = labels.some((label) => ORDER_STATUS_LABELS.has(label));
    const isLineStatus = labels.some((label) => LINE_STATUS_LABELS.has(label));

    if (isOrderStatus && !isLineStatus) return 'ORDER_STATUS';
    if (isLineStatus && !isOrderStatus) return 'LINE_ITEM_STATUS';
    return entry?.actionType;
};

const formatLabelList = (labels) => labels.join(', ');

/**
 * Build a clear, specific detail line for a status change, e.g.
 * "Order status added: Paid" or "Order status tag removed: Paid".
 * Non-status entries keep their original backend detail.
 */
export const getHistoryActionDetail = (entry) => {
    if (!isStatusEntry(entry)) return entry?.actionDetail || '—';

    const resolvedType = resolveStatusActionType(entry);
    const noun = resolvedType === 'ORDER_STATUS' ? 'Order status' : 'Line item status';

    const prevLabels = parseStatusLabels(entry?.previousValue);
    const nextLabels = parseStatusLabels(entry?.newValue);
    const added = nextLabels.filter((label) => !prevLabels.includes(label));
    const removed = prevLabels.filter((label) => !nextLabels.includes(label));

    if (added.length > 0 && removed.length > 0) {
        return `${noun} updated — added: ${formatLabelList(added)}; removed: ${formatLabelList(removed)}`;
    }
    if (added.length > 0) {
        return `${noun} added: ${formatLabelList(added)}`;
    }
    if (removed.length > 0) {
        return `${noun} tag removed: ${formatLabelList(removed)}`;
    }
    return `${noun} updated`;
};

/**
 * Part numbers carry a 3-character prefix that isn't meaningful to users, so we drop
 * the first 3 characters and show the remainder (e.g. "CBLGFS-56T" -> "GFS-56T").
 */
export const formatPartNumber = (prcpart) => {
    const value = String(prcpart ?? '').trim();
    if (value === '') return '';
    return value.slice(3);
};

/** Build the "Line X / <part>" label for a history entry, with the part prefix stripped. */
export const getLinePartLabel = (entry) => {
    if (!entry?.orderLineId) return '—';
    return `Line ${entry.lineItem || ''} / ${formatPartNumber(entry.prcpart)}`;
};

const formatQuotedLabels = (labels) => labels.map((label) => `"${label}"`).join(', ');

const getCommentDetails = (entry) => {
    const detail = entry?.actionDetail || '';
    if (detail === 'Comment deleted' || /deleted|removed/i.test(detail)) return 'Comment removed';
    if (/added/i.test(detail)) return 'Comment added';
    if (/updated/i.test(detail)) return 'Comment updated';

    const prevBlank = isBlankValue(entry?.previousValue);
    const nextBlank = isBlankValue(entry?.newValue);
    if (prevBlank && !nextBlank) return 'Comment added';
    if (!prevBlank && nextBlank) return 'Comment removed';
    return 'Comment updated';
};

const quoteName = (name) => {
    const cleaned = String(name ?? '').trim();
    if (!cleaned) return 'link';
    return `"${cleaned}"`;
};

/** Details text for Order Access add/remove/update, e.g. `"Engineering Docs" was added`. */
const getOrderAccessDetails = (entry) => {
    const hint = entry?._orderAccessHint;
    const action = hint?.action
        || (/removed|deleted/i.test(entry?.actionDetail || '') ? 'delete'
            : /updated/i.test(entry?.actionDetail || '') ? 'update'
                : 'add');

    const addedName = hint?.urlName || entry?.newValue;
    const removedName = hint?.previousUrlName || hint?.urlName || entry?.previousValue || entry?.newValue;

    // Legacy backend placeholder rows (true/false) without a local name hint.
    if (!hint && (isBoolishHistoryValue(addedName) || isBoolishHistoryValue(removedName))) {
        const nextOn = ['true', '1'].includes(String(entry?.newValue ?? '').trim().toLowerCase());
        return nextOn ? 'Order Access link added' : 'Order Access link removed';
    }

    if (action === 'delete') return `${quoteName(removedName)} was removed`;
    if (action === 'update') {
        const fromName = hint?.previousUrlName || entry?.previousValue;
        const toName = hint?.urlName || entry?.newValue;
        if (!isBlankValue(fromName) && !isBlankValue(toName) && String(fromName) !== String(toName)
            && !isBoolishHistoryValue(fromName) && !isBoolishHistoryValue(toName)) {
            return `${quoteName(fromName)} was updated to ${quoteName(toName)}`;
        }
        return `${quoteName(toName || fromName)} was updated`;
    }
    return `${quoteName(addedName)} was added`;
};

const getStatusDetails = (entry, resolvedType) => {
    const prevLabels = parseStatusLabels(entry?.previousValue);
    const nextLabels = parseStatusLabels(entry?.newValue);
    const added = nextLabels.filter((label) => !prevLabels.includes(label));
    const removed = prevLabels.filter((label) => !nextLabels.includes(label));

    let detail;
    if (added.length > 0 && removed.length > 0) {
        detail = `Tag changed from ${formatQuotedLabels(removed)} to ${formatQuotedLabels(added)}`;
    } else if (added.length > 0) {
        detail = `Tag ${formatQuotedLabels(added)} added`;
    } else if (removed.length > 0) {
        detail = `Tag ${formatQuotedLabels(removed)} removed`;
    } else {
        detail = 'Tag updated';
    }

    if (resolvedType === 'LINE_ITEM_STATUS' && entry?.orderLineId) {
        return `${getLinePartLabel(entry)}: ${detail}`;
    }
    return detail;
};

/**
 * Combined Detail + Line/Part text for the single-order history modal.
 * Action type is shown separately, so this omits "Order Status" / "Line Status" prefixes.
 */
export const getHistoryDetails = (entry) => {
    if (isOrderAccessEntry(entry)) return getOrderAccessDetails(entry);
    if (isCommentEntry(entry)) return getCommentDetails(entry);
    if (isStatusEntry(entry)) return getStatusDetails(entry, resolveStatusActionType(entry));

    const detail = entry?.actionDetail || '—';
    const linePart = getLinePartLabel(entry);
    return linePart !== '—' ? `${linePart}: ${detail}` : detail;
};

/** Same display as Notes column (HoverPO): firstname.lastname from email when present. */
export const formatChangedByDisplay = (value) => {
    if (!value) return '—';
    const cleaned = String(value).trim();
    if (!cleaned || cleaned === '—' || cleaned.toLowerCase() === 'null') return '—';
    const display = cleaned.split('@')[0];
    return display || cleaned;
};

/** Prefer changedBy, then common alternate fields the backend may send. */
export const resolveHistoryChangedBy = (entry) =>
    formatChangedByDisplay(
        entry?.changedBy
        || entry?.lastEditedBy
        || entry?.user
        || entry?.username
        || entry?.userName
        || ''
    );

const isBlankValue = (value) => value === null || value === undefined || String(value).trim() === '';

const isEmptyStatusValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[]') return true;

    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            // Empty array, or an array whose tags have no meaningful label, means "no status".
            if (parsed.length === 0) return true;
            const hasRealTag = parsed.some((tag) => {
                if (tag && typeof tag === 'object') {
                    return String(tag.label ?? '').trim() !== '';
                }
                return String(tag ?? '').trim() !== '';
            });
            return !hasRealTag;
        }
    } catch (err) {
        // Not JSON — treat as a plain, non-empty value.
    }
    return false;
};

/**
 * Compute the Previous/New value pair for an Order Status change so the history reflects
 * the actual delta of that action instead of the full tag list stored in the record.
 * - Added tag  → Previous shows the status before the change; New shows only the added tag(s).
 * - Removed tag → Previous shows only the removed tag(s); New shows "No Status".
 * - Replaced tag → Previous shows the removed tag(s); New shows the added tag(s).
 * Falls back to the stored before/after when no delta can be determined.
 */
const getOrderStatusDeltaValues = (entry) => {
    const prevTags = parseStatusTags(entry?.previousValue);
    const nextTags = parseStatusTags(entry?.newValue);
    const prevLabels = prevTags.map((tag) => tag.label);
    const nextLabels = nextTags.map((tag) => tag.label);

    const addedTags = nextTags.filter((tag) => !prevLabels.includes(tag.label));
    const removedTags = prevTags.filter((tag) => !nextLabels.includes(tag.label));

    if (addedTags.length > 0 && removedTags.length > 0) {
        return {
            previousValue: serializeStatusTags(removedTags),
            newValue: serializeStatusTags(addedTags),
        };
    }

    if (addedTags.length > 0) {
        return {
            previousValue: prevTags.length > 0 ? entry.previousValue : NO_STATUS_LABEL,
            newValue: serializeStatusTags(addedTags),
        };
    }

    if (removedTags.length > 0) {
        return {
            previousValue: serializeStatusTags(removedTags),
            newValue: NO_STATUS_LABEL,
        };
    }

    return {
        previousValue: isEmptyStatusValue(entry?.previousValue) ? NO_STATUS_LABEL : entry.previousValue,
        newValue: isEmptyStatusValue(entry?.newValue) ? NO_STATUS_LABEL : entry.newValue,
    };
};

/**
 * Resolve the Previous/New value pair shown for a history entry.
 * - Deleted comments: backend stores the removed text in newValue, so we surface
 *   it as the previous value and mark the new value as removed.
 * - Order Status changes: show only the tag that was actually added/removed/replaced.
 * - Other status changes: an empty/absent tag array is shown as "No Status" so added and
 *   removed tags read as a clear before-and-after instead of "-" or an empty badge.
 */
export const getHistoryDisplayValues = (entry) => {
    if (isDeletedCommentEntry(entry)) {
        return {
            previousValue: entry.previousValue || entry.newValue,
            newValue: 'Removed',
        };
    }

    if (isOrderAccessEntry(entry)) {
        const hint = entry?._orderAccessHint;
        const action = hint?.action
            || (/removed|deleted/i.test(entry?.actionDetail || '') ? 'delete'
                : /updated/i.test(entry?.actionDetail || '') ? 'update'
                    : 'add');
        const addedName = hint?.urlName || entry?.newValue;
        const removedName = hint?.previousUrlName || hint?.urlName || entry?.previousValue || entry?.newValue;

        // Hide true/false placeholders when we don't have the real link name yet.
        if (!hint && (isBoolishHistoryValue(entry?.previousValue) || isBoolishHistoryValue(entry?.newValue))) {
            return { previousValue: '—', newValue: '—' };
        }

        if (action === 'delete') {
            return {
                previousValue: isBlankValue(removedName) || isBoolishHistoryValue(removedName) ? '—' : removedName,
                newValue: 'Removed',
            };
        }
        if (action === 'update') {
            const prev = hint?.previousUrlName || entry?.previousValue;
            const next = addedName;
            return {
                previousValue: isBlankValue(prev) || isBoolishHistoryValue(prev) ? '—' : prev,
                newValue: isBlankValue(next) || isBoolishHistoryValue(next) ? '—' : next,
            };
        }
        const prev = hint?.previousUrlName || entry?.previousValue;
        return {
            previousValue: isBlankValue(prev) || isBoolishHistoryValue(prev) ? '—' : prev,
            newValue: isBlankValue(addedName) || isBoolishHistoryValue(addedName) ? '—' : addedName,
        };
    }

    if (STATUS_ACTION_TYPES.includes(entry?.actionType)) {
        if (resolveStatusActionType(entry) === 'ORDER_STATUS') {
            return getOrderStatusDeltaValues(entry);
        }
        return {
            previousValue: isEmptyStatusValue(entry?.previousValue) ? NO_STATUS_LABEL : entry.previousValue,
            newValue: isEmptyStatusValue(entry?.newValue) ? NO_STATUS_LABEL : entry.newValue,
        };
    }

    if (isCommentEntry(entry)) {
        return {
            previousValue: isBlankValue(entry?.previousValue) ? NO_COMMENT_LABEL : entry.previousValue,
            newValue: isBlankValue(entry?.newValue) ? NO_COMMENT_LABEL : entry.newValue,
        };
    }

    return {
        previousValue: entry?.previousValue,
        newValue: entry?.newValue,
    };
};
